import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Send, Mail, Phone, CheckCircle, Loader2, Package, Minus, Plus, Trash2, ShoppingBag, Upload, Palette, X, FileImage, ChevronRight, ArrowRight } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";

// Import product images
import bolKraft from "@/assets/products/bol-kraft-500ml.jpg";
import saladierCarre500 from "@/assets/products/saladier-carre-500ml.jpg";
import barquetteBateau300 from "@/assets/products/barquette-bateau-300ml.png";
import potSoupe8oz from "@/assets/products/pot-soupe-8oz.png";
import potGlace90 from "@/assets/products/pot-glace-90ml.jpg";
import gobeletKraft110 from "@/assets/products/gobelet-kraft-110ml.png";
import smoothie250 from "@/assets/products/gobelet-smoothie-250ml.png";
import pailleKraft from "@/assets/products/paille-kraft.jpg";
import sacKraftPoignees from "@/assets/products/sac-kraft-poignees.png";
import pastaBoxImg from "@/assets/products/pasta-box-500ml.png";
import potSauceImg from "@/assets/products/pot-sauce-single.png";
import potDessertImg from "@/assets/products/pot-dessert.png";
import boiteAccompagnementImg from "@/assets/products/boite-accompagnement-fc0.png";
import sacBretellePetit from "@/assets/products/sac-bretelle-petit.png";
import couvertsImg from "@/assets/products/couverts.png";
import sacPoubelleImg from "@/assets/products/sac-poubelle.png";
import bobineThermiqueImg from "@/assets/products/bobine-thermique-cb.png";
import bobineEssuieMainsImg from "@/assets/products/bobine-essuie-mains.png";
import pelleHotdogImg from "@/assets/products/pelle-hotdog-petite.png";

import serviettesBlanches from "@/assets/products/serviettes-blanches.png";
import gantsNitrileBleus from "@/assets/products/gants-nitrile-bleus.png";
import bolPersonnalise from "@/assets/products/bol-personnalise.png";
import sacPersonnalise from "@/assets/products/sac-personnalise-ams.png";
import gobeletBlancPerso from "@/assets/products/gobelet-blanc-200ml.jpg";
import serviettesPerso from "@/assets/products/serviettes-blanches.png";

// Helper function to extract pieces per carton from conditionnement string
const getPiecesPerCarton = (conditionnement: string): number => {
  const match = conditionnement.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
};

// Produits standards (sans personnalisation) avec conditionnements
const standardProducts = [
  { 
    id: "bols-kraft", 
    name: "Bols Kraft", 
    image: bolKraft, 
    sizes: [
      { label: "500ml Kraft", conditionnement: "200 pcs/carton" },
      { label: "500ml Blanc", conditionnement: "200 pcs/carton" },
      { label: "750ml Kraft", conditionnement: "200 pcs/carton" },
      { label: "750ml Blanc", conditionnement: "200 pcs/carton" },
      { label: "1000ml Kraft", conditionnement: "200 pcs/carton" },
      { label: "1000ml Blanc", conditionnement: "200 pcs/carton" },
      { label: "1100ml Kraft", conditionnement: "200 pcs/carton" },
      { label: "1300ml Kraft", conditionnement: "200 pcs/carton" },
      { label: "1300ml Blanc", conditionnement: "200 pcs/carton" },
    ]
  },
  { 
    id: "saladiers-carres", 
    name: "Saladiers carrés", 
    image: saladierCarre500, 
    sizes: [
      { label: "500ml", conditionnement: "200 pcs/carton" },
      { label: "650ml", conditionnement: "200 pcs/carton" },
      { label: "1000ml", conditionnement: "200 pcs/carton" },
      { label: "1300ml", conditionnement: "200 pcs/carton" },
    ]
  },
  { 
    id: "barquettes-bateau", 
    name: "Barquettes bateau", 
    image: barquetteBateau300, 
    sizes: [
      { label: "300ml", conditionnement: "1000 pcs/carton" },
      { label: "400ml", conditionnement: "500 pcs/carton" },
      { label: "680ml", conditionnement: "500 pcs/carton" },
      { label: "1000ml", conditionnement: "500 pcs/carton" },
    ]
  },
  { 
    id: "pots-soupe", 
    name: "Pots à soupe", 
    image: potSoupe8oz, 
    sizes: [
      { label: "8oz (240ml)", conditionnement: "300 pcs/carton" },
      { label: "12oz (350ml)", conditionnement: "300 pcs/carton" },
      { label: "16oz (480ml)", conditionnement: "300 pcs/carton" },
      { label: "24oz (720ml)", conditionnement: "300 pcs/carton" },
      { label: "32oz (960ml)", conditionnement: "300 pcs/carton" },
    ]
  },
  { 
    id: "pots-glace", 
    name: "Pots à glace", 
    image: potGlace90, 
    sizes: [
      { label: "90ml", conditionnement: "1000 pcs/carton" },
      { label: "110ml", conditionnement: "1000 pcs/carton" },
      { label: "180ml", conditionnement: "1000 pcs/carton" },
      { label: "230ml", conditionnement: "1000 pcs/carton" },
    ]
  },
  { 
    id: "gobelets-carton", 
    name: "Gobelets carton", 
    image: gobeletKraft110, 
    sizes: [
      { label: "110ml Kraft", conditionnement: "1000 pcs/carton" },
      { label: "110ml Blanc", conditionnement: "1000 pcs/carton" },
      { label: "200ml Kraft", conditionnement: "1000 pcs/carton" },
      { label: "200ml Blanc", conditionnement: "1000 pcs/carton" },
      { label: "230ml Kraft", conditionnement: "1000 pcs/carton" },
      { label: "230ml Blanc", conditionnement: "1000 pcs/carton" },
      { label: "350ml Kraft", conditionnement: "1000 pcs/carton" },
      { label: "350ml Blanc", conditionnement: "1000 pcs/carton" },
    ]
  },
  { 
    id: "gobelets-smoothie", 
    name: "Gobelets smoothie rPET", 
    image: smoothie250, 
    sizes: [
      { label: "250ml", conditionnement: "1000 pcs/carton" },
      { label: "350ml", conditionnement: "1000 pcs/carton" },
      { label: "470ml", conditionnement: "1000 pcs/carton" },
      { label: "590ml", conditionnement: "1000 pcs/carton" },
    ]
  },
  { 
    id: "pailles", 
    name: "Pailles papier", 
    image: pailleKraft, 
    sizes: [
      { label: "Kraft Ø6mm", conditionnement: "5000 pcs/carton" },
      { label: "Kraft Ø8mm", conditionnement: "5000 pcs/carton" },
      { label: "Noire Ø6mm", conditionnement: "5000 pcs/carton" },
      { label: "Noire Ø8mm", conditionnement: "5000 pcs/carton" },
    ]
  },
  { 
    id: "sacs-kraft", 
    name: "Sacs kraft", 
    image: sacKraftPoignees, 
    sizes: [
      { label: "SOS petit", conditionnement: "250 pcs/carton" },
      { label: "SOS moyen", conditionnement: "250 pcs/carton" },
      { label: "SOS grand", conditionnement: "250 pcs/carton" },
      { label: "Anses torsadées petit", conditionnement: "250 pcs/carton" },
      { label: "Anses torsadées moyen", conditionnement: "250 pcs/carton" },
      { label: "Anses torsadées grand", conditionnement: "250 pcs/carton" },
      { label: "Anses plates", conditionnement: "250 pcs/carton" },
    ]
  },
  { 
    id: "pasta-box", 
    name: "Pasta Box", 
    image: pastaBoxImg, 
    sizes: [
      { label: "500ml", conditionnement: "500 pcs/carton" },
      { label: "750ml", conditionnement: "300 pcs/carton" },
      { label: "1000ml", conditionnement: "200 pcs/carton" },
    ]
  },
  { 
    id: "pelle-hotdog", 
    name: "Pelles à hot dog", 
    image: pelleHotdogImg, 
    sizes: [
      { label: "Petite (160x50x30mm)", conditionnement: "1000 pcs/carton" },
      { label: "Moyenne (208x70x75mm)", conditionnement: "400 pcs/carton" },
      { label: "Grande (260x120x70mm)", conditionnement: "200 pcs/carton" },
    ]
  },
  { 
    id: "boite-accompagnement", 
    name: "Boîtes accompagnement", 
    image: boiteAccompagnementImg, 
    sizes: [
      { label: "FC0 - Petite", conditionnement: "Sur demande" },
      { label: "FC1 - Grande", conditionnement: "Sur demande" },
    ]
  },
  { 
    id: "pot-sauce", 
    name: "Pots à sauce", 
    image: potSauceImg, 
    sizes: [
      { label: "30ml", conditionnement: "500 pcs/carton" },
      { label: "60ml", conditionnement: "500 pcs/carton" },
    ]
  },
  { 
    id: "pot-dessert", 
    name: "Pots à dessert", 
    image: potDessertImg, 
    sizes: [
      { label: "Pot transparent 250ml", conditionnement: "500 pcs/carton" },
      { label: "Pot couvercle noir 250ml", conditionnement: "500 pcs/carton" },
    ]
  },
  { 
    id: "serviettes", 
    name: "Serviettes", 
    image: serviettesBlanches, 
    sizes: [
      { label: "25x32cm", conditionnement: "5000 pcs/carton" },
    ]
  },
  { 
    id: "bobine-essuie-mains", 
    name: "Bobines essuie-mains", 
    image: bobineEssuieMainsImg, 
    sizes: [
      { label: "1000 feuilles", conditionnement: "6 bobines/colis" },
    ]
  },
  { 
    id: "bobine-thermique", 
    name: "Bobines thermiques", 
    image: bobineThermiqueImg, 
    sizes: [
      { label: "CB 57x40mm", conditionnement: "50 pcs/carton" },
      { label: "Caisse 80x80mm", conditionnement: "30 pcs/carton" },
    ]
  },
  { 
    id: "gants", 
    name: "Gants", 
    image: gantsNitrileBleus, 
    sizes: [
      { label: "Nitrile bleu S", conditionnement: "10 boîtes/carton" },
      { label: "Nitrile bleu M", conditionnement: "10 boîtes/carton" },
      { label: "Nitrile bleu L", conditionnement: "10 boîtes/carton" },
      { label: "Nitrile bleu XL", conditionnement: "10 boîtes/carton" },
      { label: "Nitrile noir S", conditionnement: "10 boîtes/carton" },
      { label: "Nitrile noir M", conditionnement: "10 boîtes/carton" },
      { label: "Nitrile noir L", conditionnement: "10 boîtes/carton" },
      { label: "Nitrile noir XL", conditionnement: "10 boîtes/carton" },
      { label: "Vinyle S", conditionnement: "10 boîtes/carton" },
      { label: "Vinyle M", conditionnement: "10 boîtes/carton" },
      { label: "Vinyle L", conditionnement: "10 boîtes/carton" },
      { label: "Vinyle XL", conditionnement: "10 boîtes/carton" },
    ]
  },
  { 
    id: "film-alimentaire", 
    name: "Films alimentaires", 
    image: serviettesBlanches, 
    sizes: [
      { label: "Film étirable 30cm x 300m", conditionnement: "1 rouleau/carton" },
      { label: "Film aluminium 30cm x 300m", conditionnement: "1 rouleau/carton" },
    ]
  },
];

// Produits personnalisables
const customizableProducts = [
  { id: "bol-personnalise", name: "Bol personnalisé", image: bolPersonnalise },
  { id: "sac-personnalise", name: "Sac personnalisé", image: sacPersonnalise },
  { id: "gobelet-personnalise", name: "Gobelet personnalisé", image: gobeletBlancPerso },
  { id: "serviette-personnalisee", name: "Serviette personnalisée", image: serviettesPerso },
];

interface SelectedProduct {
  id: string;
  name: string;
  size: string;
  quantity: number;
  conditionnement: string;
}

interface CustomProduct {
  id: string;
  name: string;
  details: string;
  logoFile: File | null;
  logoPreview: string | null;
}

// Zod schema for form validation
const quoteSchema = z.object({
  restaurantName: z.string()
    .trim()
    .min(1, "Le nom de l'établissement est requis")
    .max(200, "Le nom de l'établissement est trop long (max 200 caractères)"),
  email: z.string()
    .trim()
    .email("Adresse email invalide")
    .max(255, "L'adresse email est trop longue"),
  phone: z.string()
    .trim()
    .max(20, "Numéro de téléphone trop long")
    .optional()
    .or(z.literal("")),
  message: z.string()
    .trim()
    .max(5000, "Le message est trop long (max 5000 caractères)")
    .optional()
    .or(z.literal("")),
});

type QuoteFormData = z.infer<typeof quoteSchema>;

const SectionNumber = ({ number, label }: { number: string; label: string }) => (
  <div className="flex items-center gap-5 mb-8">
    <div className="relative">
      <span className="font-display text-7xl md:text-9xl font-bold bg-gradient-to-b from-primary/20 to-primary/5 bg-clip-text text-transparent select-none leading-none">
        {number}
      </span>
      <div className="absolute -bottom-1 left-0 w-full h-[3px] bg-gradient-to-r from-primary via-primary/60 to-transparent rounded-full" />
    </div>
    <div className="flex-1">
      <h2 className="font-display text-3xl md:text-4xl font-bold text-accent leading-tight">
        {label}
      </h2>
    </div>
  </div>
);

const Devis = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
  const [customProducts, setCustomProducts] = useState<CustomProduct[]>([]);
  const [formData, setFormData] = useState({
    restaurantName: "",
    email: "",
    phone: "",
    message: "",
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const addProduct = (productId: string, size: string, conditionnement: string) => {
    const product = standardProducts.find(p => p.id === productId);
    if (!product) return;

    const existingIndex = selectedProducts.findIndex(
      p => p.id === productId && p.size === size
    );

    if (existingIndex >= 0) {
      setSelectedProducts(prev => prev.map((p, i) => 
        i === existingIndex ? { ...p, quantity: p.quantity + 1 } : p
      ));
    } else {
      setSelectedProducts(prev => [...prev, {
        id: productId,
        name: product.name,
        size,
        quantity: 1,
        conditionnement,
      }]);
    }

    toast({
      title: "Produit ajouté",
      description: `${product.name} (${size}) ajouté à votre demande`,
    });
  };

  const addCustomProduct = (productId: string) => {
    const product = customizableProducts.find(p => p.id === productId);
    if (!product) return;

    if (customProducts.some(p => p.id === productId)) {
      toast({
        title: "Déjà ajouté",
        description: `${product.name} est déjà dans votre sélection personnalisée`,
        variant: "destructive",
      });
      return;
    }

    setCustomProducts(prev => [...prev, {
      id: productId,
      name: product.name,
      details: "",
      logoFile: null,
      logoPreview: null,
    }]);

    toast({
      title: "Produit personnalisé ajouté",
      description: `${product.name} ajouté - Vous pouvez maintenant ajouter votre logo et détails`,
    });
  };

  const updateCustomProductDetails = (id: string, details: string) => {
    setCustomProducts(prev => prev.map(p => 
      p.id === id ? { ...p, details } : p
    ));
  };

  const handleLogoUpload = (id: string, file: File | null) => {
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/svg+xml', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast({
        title: "Format non supporté",
        description: "Veuillez utiliser un fichier PNG, JPG, SVG, WebP ou PDF",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "Fichier trop volumineux",
        description: "La taille maximale est de 10 Mo",
        variant: "destructive",
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setCustomProducts(prev => prev.map(p => 
        p.id === id ? { ...p, logoFile: file, logoPreview: reader.result as string } : p
      ));
    };
    reader.readAsDataURL(file);
  };

  const removeLogoFromCustomProduct = (id: string) => {
    setCustomProducts(prev => prev.map(p => 
      p.id === id ? { ...p, logoFile: null, logoPreview: null } : p
    ));
  };

  const removeCustomProduct = (id: string) => {
    setCustomProducts(prev => prev.filter(p => p.id !== id));
  };

  const updateQuantity = (index: number, delta: number) => {
    setSelectedProducts(prev => prev.map((p, i) => {
      if (i === index) {
        const newQty = Math.max(1, p.quantity + delta);
        return { ...p, quantity: newQty };
      }
      return p;
    }));
  };

  const removeProduct = (index: number) => {
    setSelectedProducts(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationErrors({});

    const hasProducts = selectedProducts.length > 0 || customProducts.length > 0;
    if (!hasProducts) {
      toast({
        title: "Aucun produit sélectionné",
        description: "Veuillez sélectionner au moins un produit pour votre demande de devis.",
        variant: "destructive",
      });
      return;
    }

    const result = quoteSchema.safeParse(formData);
    if (!result.success) {
      const errors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          errors[err.path[0] as string] = err.message;
        }
      });
      setValidationErrors(errors);
      toast({
        title: "Erreur de validation",
        description: "Veuillez corriger les erreurs dans le formulaire.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const uploadedLogos: { productName: string; filePath: string; fileName: string }[] = [];
      
      for (const customProduct of customProducts) {
        if (customProduct.logoFile) {
          const timestamp = Date.now();
          const sanitizedName = customProduct.logoFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
          const filePath = `${timestamp}-${sanitizedName}`;
          
          const { error: uploadError } = await supabase.storage
            .from('logos')
            .upload(filePath, customProduct.logoFile);
          
          if (uploadError) {
            console.error('Logo upload error:', uploadError);
            throw new Error(`Erreur lors de l'upload du logo: ${uploadError.message}`);
          }
          
          uploadedLogos.push({
            productName: customProduct.name,
            filePath,
            fileName: customProduct.logoFile.name,
          });
        }
      }

      let productsText = "";
      if (selectedProducts.length > 0) {
        productsText = "PRODUITS STANDARDS:\n" + selectedProducts
          .map(p => `- ${p.name} (${p.size}) x${p.quantity}`)
          .join("\n");
      }

      if (customProducts.length > 0) {
        const customText = "\n\nPRODUITS PERSONNALISÉS:\n" + customProducts
          .map(p => {
            let text = `- ${p.name}`;
            if (p.details) text += `\n  Détails: ${p.details}`;
            if (p.logoFile) text += `\n  Logo: ${p.logoFile.name} (fichier joint en PDF)`;
            return text;
          })
          .join("\n");
        productsText += customText;
      }
      
      const fullMessage = `${productsText}\n\n${formData.message || "Aucun commentaire supplémentaire."}`;

      const { data, error } = await supabase.functions.invoke('send-quote-request', {
        body: { 
          ...result.data, 
          message: fullMessage,
          logos: uploadedLogos,
        },
      });

      if (error) throw error;

      setIsSubmitted(true);
      toast({
        title: "Demande envoyée !",
        description: "Notre équipe commerciale vous contactera dans les plus brefs délais.",
      });
    } catch (error: any) {
      console.error('Submit error:', error);
      toast({
        title: "Erreur",
        description: error.message || "Une erreur est survenue. Veuillez réessayer ou nous contacter directement.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalProducts = selectedProducts.length + customProducts.length;

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-32 pb-20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="max-w-2xl mx-auto text-center"
            >
              <div className="w-24 h-24 rounded-full bg-eco/10 border-2 border-eco/20 flex items-center justify-center mx-auto mb-10">
                <CheckCircle className="w-12 h-12 text-eco" />
              </div>
              <h1 className="font-display text-4xl md:text-5xl font-bold text-accent mb-6">
                Demande envoyée
              </h1>
              <div className="w-16 h-[2px] bg-primary mx-auto mb-6" />
              <p className="text-muted-foreground text-lg mb-10 leading-relaxed">
                Merci pour votre demande. Notre équipe commerciale l'a bien reçue et vous 
                contactera dans les plus brefs délais à l'adresse : <strong className="text-foreground">{formData.email}</strong>
              </p>
              <Button
                variant="kraft"
                size="lg"
                onClick={() => {
                  setIsSubmitted(false);
                  setFormData({ restaurantName: "", email: "", phone: "", message: "" });
                  setSelectedProducts([]);
                  setCustomProducts([]);
                }}
              >
                Faire une nouvelle demande
                <ArrowRight className="w-4 h-4" />
              </Button>
            </motion.div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEOHead 
        title="Demander un Devis - AMS DISTRIB"
        description="Demandez un devis personnalisé pour vos emballages alimentaires. Sélectionnez vos produits et recevez une offre adaptée à vos besoins."
      />
      <Header />
      <main>
        {/* Hero - Premium Dark */}
        <section className="relative pt-32 pb-24 bg-section-accent overflow-hidden">
          {/* Luxury decorative elements */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_50%,hsl(215_90%_50%/0.2),transparent_60%)]" />
          <div className="absolute top-0 right-0 w-1/2 h-full bg-[radial-gradient(ellipse_at_80%_20%,hsl(215_90%_50%/0.12),transparent_50%)]" />
          <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
          
          {/* Animated floating orbs */}
          <motion.div 
            className="absolute top-20 right-[15%] w-64 h-64 rounded-full bg-primary/5 blur-3xl"
            animate={{ y: [0, -20, 0], scale: [1, 1.1, 1] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute bottom-10 left-[10%] w-48 h-48 rounded-full bg-primary/8 blur-3xl"
            animate={{ y: [0, 15, 0], scale: [1, 0.95, 1] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          />
          
          <div className="container mx-auto px-4 relative z-10">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-primary-foreground/40 mb-16">
              <Link to="/" className="hover:text-primary-foreground/70 transition-colors">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-primary-foreground/70 font-medium">Demande de devis</span>
            </nav>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-3xl"
            >
              <motion.div 
                className="flex items-center gap-4 mb-8"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <div className="w-12 h-[2px] bg-gradient-to-r from-primary to-primary/40" />
                <span className="text-primary font-bold tracking-[0.2em] uppercase text-xs">
                  Contact commercial
                </span>
                <div className="w-8 h-[2px] bg-gradient-to-r from-primary/40 to-transparent" />
              </motion.div>
              <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-bold text-primary-foreground mb-8 leading-[1.05]">
                Demander<br />
                un <span className="bg-gradient-to-r from-primary via-blue-400 to-primary bg-clip-text text-transparent">Devis</span>
              </h1>
              <p className="text-primary-foreground/50 text-lg md:text-xl max-w-xl leading-relaxed">
                Composez votre commande en quelques clics et recevez une offre personnalisée sous 24h.
              </p>
              
              {/* Trust indicators */}
              <motion.div 
                className="flex flex-wrap gap-6 mt-10 pt-10 border-t border-primary-foreground/10"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.6 }}
              >
                {[
                  { value: "24h", label: "Délai de réponse" },
                  { value: "100%", label: "Devis gratuit" },
                  { value: "FSC", label: "Certifié" },
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <p className="font-display text-2xl font-bold text-primary">{stat.value}</p>
                    <p className="text-xs text-primary-foreground/40 mt-1">{stat.label}</p>
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Section 01 - Produits standards */}
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-7xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="mb-10"
              >
                <SectionNumber number="01" label="Produits standards" />
                <p className="text-muted-foreground -mt-4 mb-6 ml-[calc(theme(spacing.5)+theme(fontSize.9xl)+theme(spacing.5))] md:ml-0">Sélectionnez les tailles souhaitées</p>
                <div className="w-full h-px bg-gradient-to-r from-border via-primary/20 to-border" />
              </motion.div>
              
              <Carousel
                opts={{ align: "start", loop: true }}
                className="w-full"
              >
                <CarouselContent className="-ml-3 md:-ml-4">
                  {standardProducts.map((product) => (
                    <CarouselItem key={product.id} className="pl-3 md:pl-4 basis-[280px] md:basis-[300px]">
                      <motion.div 
                        whileHover={{ y: -6, scale: 1.02 }}
                        transition={{ type: "spring", stiffness: 300, damping: 20 }}
                        className="group bg-background rounded-2xl border border-border overflow-hidden hover:border-primary/50 hover:shadow-float transition-shadow duration-500 h-full"
                      >
                        <div className="h-36 bg-gradient-to-b from-secondary/40 via-card to-background flex items-center justify-center p-6 relative overflow-hidden">
                          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                          <img
                            src={product.image}
                            alt={product.name}
                            className="max-h-full object-contain group-hover:scale-110 transition-transform duration-700 ease-out relative z-10"
                          />
                        </div>
                        <div className="p-5 border-t border-border/50">
                          <h3 className="font-display font-bold text-foreground mb-4 text-base tracking-tight">
                            {product.name}
                          </h3>
                          <div className="flex flex-wrap gap-2 max-h-[160px] overflow-y-auto">
                            {product.sizes.map((size) => (
                              <button
                                key={size.label}
                                onClick={() => addProduct(product.id, size.label, size.conditionnement)}
                                className="group/btn relative px-3 py-2 text-xs bg-secondary/60 hover:bg-primary hover:text-primary-foreground rounded-lg transition-all duration-300 font-medium text-left border border-border/50 hover:border-primary hover:shadow-lg hover:shadow-primary/20"
                                title={size.conditionnement || undefined}
                              >
                                <span className="block font-bold">{size.label}</span>
                                {size.conditionnement && (
                                  <span className="block text-[10px] text-muted-foreground group-hover/btn:text-primary-foreground/70 mt-0.5">{size.conditionnement}</span>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="hidden md:flex -left-5 bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground border-0 shadow-lg" />
                <CarouselNext className="hidden md:flex -right-5 bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground border-0 shadow-lg" />
              </Carousel>
            </div>
          </div>
        </section>

        {/* Section 02 - Produits personnalisés */}
        <section className="py-16 md:py-20 bg-card border-y border-border">
          <div className="container mx-auto px-4">
            <div className="max-w-7xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="mb-10"
              >
                <SectionNumber number="02" label="Personnalisation" />
                <p className="text-muted-foreground -mt-4 mb-6">
                  Ajoutez votre logo sur nos produits
                  <span className="ml-3 inline-flex items-center px-3 py-1 bg-gradient-to-r from-primary/15 to-primary/5 text-primary text-xs rounded-full font-bold border border-primary/20">
                    ✦ Sur mesure
                  </span>
                </p>
                <div className="w-full h-px bg-gradient-to-r from-border via-primary/20 to-border" />
              </motion.div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {customizableProducts.map((product) => {
                  const isSelected = customProducts.some(p => p.id === product.id);
                  return (
                    <motion.button
                      key={product.id}
                      whileHover={{ y: -4 }}
                      onClick={() => addCustomProduct(product.id)}
                      disabled={isSelected}
                      className={`relative bg-background rounded-2xl border-2 overflow-hidden transition-all text-left ${
                        isSelected 
                          ? 'border-primary shadow-lg shadow-primary/10' 
                          : 'border-border hover:border-primary/40 hover:shadow-medium'
                      }`}
                    >
                      {isSelected && (
                        <div className="absolute top-3 right-3 w-6 h-6 bg-primary rounded-full flex items-center justify-center z-10">
                          <CheckCircle className="w-4 h-4 text-primary-foreground" />
                        </div>
                      )}
                      <div className="h-28 bg-gradient-to-b from-secondary/50 to-background flex items-center justify-center p-4">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="max-h-full object-contain"
                        />
                      </div>
                      <div className="p-4 text-center">
                        <h3 className="font-display font-bold text-foreground text-sm">
                          {product.name}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-1">
                          {isSelected ? "✓ Sélectionné" : "Cliquez pour ajouter"}
                        </p>
                      </div>
                    </motion.button>
                  );
                })}
              </div>

              {/* Custom Products Details */}
              <AnimatePresence>
                {customProducts.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-4"
                  >
                    {customProducts.map((product) => (
                      <motion.div
                        key={product.id}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="bg-background rounded-2xl border border-primary/20 p-6 shadow-soft"
                      >
                        <div className="flex items-start justify-between mb-5">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                              <Palette className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                              <h4 className="font-display font-bold text-foreground text-lg">{product.name}</h4>
                              <p className="text-sm text-muted-foreground">Personnalisation sur mesure</p>
                            </div>
                          </div>
                          <button
                            onClick={() => removeCustomProduct(product.id)}
                            className="p-2.5 hover:bg-destructive/10 rounded-xl transition-colors group"
                          >
                            <X className="w-4 h-4 text-muted-foreground group-hover:text-destructive" />
                          </button>
                        </div>

                        <div className="grid md:grid-cols-2 gap-5">
                          {/* Logo Upload */}
                          <div>
                            <Label className="text-sm font-semibold mb-2 block text-foreground">
                              Votre logo (optionnel)
                            </Label>
                            {product.logoPreview ? (
                              <div className="relative border-2 border-dashed border-primary/30 rounded-xl p-4 bg-primary/5">
                                <div className="flex items-center gap-3">
                                  {product.logoFile?.type === 'application/pdf' ? (
                                    <div className="w-16 h-16 bg-secondary rounded-xl flex items-center justify-center">
                                      <FileImage className="w-8 h-8 text-primary" />
                                    </div>
                                  ) : (
                                    <img 
                                      src={product.logoPreview} 
                                      alt="Logo preview" 
                                      className="w-16 h-16 object-contain rounded-xl bg-white border border-border"
                                    />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-foreground truncate">
                                      {product.logoFile?.name}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {product.logoFile && (product.logoFile.size / 1024).toFixed(1)} Ko
                                    </p>
                                  </div>
                                  <button
                                    onClick={() => removeLogoFromCustomProduct(product.id)}
                                    className="p-2 hover:bg-destructive/10 rounded-full transition-colors"
                                  >
                                    <Trash2 className="w-4 h-4 text-destructive" />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <label 
                                className="flex flex-col items-center justify-center border-2 border-dashed border-border hover:border-primary/50 rounded-xl p-8 cursor-pointer transition-all bg-secondary/20 hover:bg-secondary/40"
                              >
                                <Upload className="w-8 h-8 text-muted-foreground mb-3" />
                                <span className="text-sm font-semibold text-foreground">
                                  Glissez votre logo ici
                                </span>
                                <span className="text-xs text-muted-foreground mt-1">
                                  PNG, JPG, SVG, PDF (max 10 Mo)
                                </span>
                                <input
                                  type="file"
                                  accept=".png,.jpg,.jpeg,.svg,.webp,.pdf"
                                  className="hidden"
                                  onChange={(e) => handleLogoUpload(product.id, e.target.files?.[0] || null)}
                                />
                              </label>
                            )}
                          </div>

                          {/* Details */}
                          <div>
                            <Label className="text-sm font-semibold mb-2 block text-foreground">
                              Informations complémentaires
                            </Label>
                            <Textarea
                              placeholder="Tailles souhaitées, quantités estimées, couleurs, emplacement du logo..."
                              value={product.details}
                              onChange={(e) => updateCustomProductDetails(product.id, e.target.value)}
                              rows={4}
                              maxLength={1000}
                              className="resize-none text-sm rounded-xl"
                            />
                            <p className="text-xs text-muted-foreground mt-1 text-right">
                              {product.details.length}/1000
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </section>

        {/* Section 03 & 04 - Selection + Form */}
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-7xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12">
                {/* Selection */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                >
                  <SectionNumber number="03" label="Votre sélection" />
                  {totalProducts > 0 && (
                    <div className="-mt-4 mb-6">
                      <span className="inline-flex items-center px-4 py-1.5 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground text-xs font-bold rounded-full shadow-lg shadow-primary/20">
                        {totalProducts} produit{totalProducts > 1 ? 's' : ''} sélectionné{totalProducts > 1 ? 's' : ''}
                      </span>
                    </div>
                  )}

                  <div className="bg-card rounded-2xl border border-border p-5 min-h-[240px]">
                    {totalProducts === 0 ? (
                      <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                        <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mb-4">
                          <ShoppingBag className="w-7 h-7 opacity-40" />
                        </div>
                        <p className="font-medium text-sm">Aucun produit sélectionné</p>
                        <p className="text-xs mt-1">Cliquez sur une taille ou un produit personnalisé ci-dessus</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {/* Standard products */}
                        {selectedProducts.length > 0 && (
                          <>
                            {customProducts.length > 0 && (
                              <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest">
                                Standards
                              </p>
                            )}
                            <AnimatePresence>
                              {selectedProducts.map((product, index) => {
                                const piecesPerCarton = getPiecesPerCarton(product.conditionnement);
                                const totalPieces = piecesPerCarton * product.quantity;
                                return (
                                  <motion.div
                                    key={`${product.id}-${product.size}`}
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="flex items-center gap-3 p-3.5 bg-secondary/40 rounded-xl border border-border/50"
                                  >
                                    <div className="flex-1 min-w-0">
                                      <p className="font-semibold text-sm text-foreground truncate">
                                        {product.name}
                                      </p>
                                      <p className="text-xs text-muted-foreground">{product.size}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => updateQuantity(index, -1)}
                                        className="w-7 h-7 rounded-lg bg-background border border-border flex items-center justify-center hover:border-primary transition-colors"
                                      >
                                        <Minus className="w-3 h-3" />
                                      </button>
                                      <span className="w-8 text-center font-bold text-sm">
                                        {product.quantity}
                                      </span>
                                      <button
                                        onClick={() => updateQuantity(index, 1)}
                                        className="w-7 h-7 rounded-lg bg-background border border-border flex items-center justify-center hover:border-primary transition-colors"
                                      >
                                        <Plus className="w-3 h-3" />
                                      </button>
                                      {piecesPerCarton > 0 && (
                                        <span className="text-xs text-primary font-bold bg-primary/10 px-2.5 py-1 rounded-lg whitespace-nowrap">
                                          = {totalPieces.toLocaleString('fr-FR')} pcs
                                        </span>
                                      )}
                                      <button
                                        onClick={() => removeProduct(index)}
                                        className="w-7 h-7 rounded-lg bg-destructive/10 text-destructive flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground transition-colors ml-1"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    </div>
                                  </motion.div>
                                );
                              })}
                            </AnimatePresence>
                          </>
                        )}

                        {/* Custom products */}
                        {customProducts.length > 0 && (
                          <>
                            {selectedProducts.length > 0 && (
                              <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest mt-5">
                                Personnalisés
                              </p>
                            )}
                            <AnimatePresence>
                              {customProducts.map((product) => (
                                <motion.div
                                  key={product.id}
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  exit={{ opacity: 0, height: 0 }}
                                  className="flex items-center gap-3 p-3.5 bg-primary/5 border border-primary/20 rounded-xl"
                                >
                                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <Palette className="w-4 h-4 text-primary" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-sm text-foreground truncate">
                                      {product.name}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {product.logoFile ? "Logo ajouté" : "Sans logo"} 
                                      {product.details && " • Détails ajoutés"}
                                    </p>
                                  </div>
                                  <button
                                    onClick={() => removeCustomProduct(product.id)}
                                    className="w-7 h-7 rounded-lg bg-destructive/10 text-destructive flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground transition-colors"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </motion.div>
                              ))}
                            </AnimatePresence>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>

                {/* Form */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                >
                  <SectionNumber number="04" label="Vos coordonnées" />

                  <form onSubmit={handleSubmit} className="bg-card rounded-2xl border border-border p-6 md:p-8 shadow-medium relative overflow-hidden">
                    {/* Decorative corner accent */}
                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-primary/5 to-transparent" />
                    <div className="absolute top-0 right-0 w-px h-16 bg-gradient-to-b from-primary/30 to-transparent" />
                    <div className="absolute top-0 right-0 h-px w-16 bg-gradient-to-l from-primary/30 to-transparent" />
                    <div className="space-y-5">
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div className="space-y-2">
                          <Label htmlFor="restaurantName" className="font-semibold text-foreground">Établissement *</Label>
                          <Input
                            id="restaurantName"
                            name="restaurantName"
                            placeholder="Nom de votre établissement"
                            value={formData.restaurantName}
                            onChange={handleChange}
                            maxLength={200}
                            required
                            className={`h-12 rounded-xl bg-background ${validationErrors.restaurantName ? 'border-destructive' : 'border-border'}`}
                          />
                          {validationErrors.restaurantName && (
                            <p className="text-xs text-destructive font-medium">{validationErrors.restaurantName}</p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="phone" className="font-semibold text-foreground">Téléphone</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            placeholder="06 XX XX XX XX"
                            value={formData.phone}
                            onChange={handleChange}
                            maxLength={20}
                            className={`h-12 rounded-xl bg-background ${validationErrors.phone ? 'border-destructive' : 'border-border'}`}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email" className="font-semibold text-foreground">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="votre@email.com"
                          value={formData.email}
                          onChange={handleChange}
                          maxLength={255}
                          required
                          className={`h-12 rounded-xl bg-background ${validationErrors.email ? 'border-destructive' : 'border-border'}`}
                        />
                        {validationErrors.email && (
                          <p className="text-xs text-destructive font-medium">{validationErrors.email}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message" className="font-semibold text-foreground">Commentaires (optionnel)</Label>
                        <Textarea
                          id="message"
                          name="message"
                          placeholder="Précisions sur les quantités, fréquence de commande..."
                          value={formData.message}
                          onChange={handleChange}
                          maxLength={5000}
                          rows={3}
                          className={`resize-none rounded-xl bg-background ${validationErrors.message ? 'border-destructive' : 'border-border'}`}
                        />
                      </div>

                      <Button
                        type="submit"
                        variant="kraft"
                        size="lg"
                        className="w-full h-14 text-base rounded-xl"
                        disabled={isSubmitting || totalProducts === 0}
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Envoi en cours...
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5" />
                            Envoyer ma demande
                            {totalProducts > 0 && (
                              <span className="ml-1 bg-primary-foreground/20 px-2 py-0.5 rounded-md text-sm">
                                {totalProducts} produit{totalProducts > 1 ? 's' : ''}
                              </span>
                            )}
                          </>
                        )}
                      </Button>

                      <p className="text-xs text-muted-foreground text-center pt-1">
                        Réponse sous 24h · Devis gratuit et sans engagement
                      </p>
                    </div>
                  </form>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Direct - Premium bar */}
        <section className="py-0">
          <div className="container mx-auto px-4">
            <div className="max-w-7xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="bg-section-accent rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6 mb-16"
              >
                <div>
                  <h3 className="font-display text-xl font-bold text-primary-foreground mb-1">
                    Besoin d'une réponse rapide ?
                  </h3>
                  <p className="text-primary-foreground/60 text-sm">
                    Notre équipe commerciale est disponible pour vous accompagner
                  </p>
                </div>
                <div className="flex flex-wrap gap-3 justify-center">
                  <a
                    href="mailto:servicecommercial@amsdistrib.fr"
                    className="inline-flex items-center gap-2.5 px-5 py-3 bg-primary-foreground/10 backdrop-blur-sm rounded-xl text-sm font-semibold text-primary-foreground hover:bg-primary-foreground/20 transition-colors border border-primary-foreground/10"
                  >
                    <Mail className="w-4 h-4" />
                    servicecommercial@amsdistrib.fr
                  </a>
                  <a
                    href="tel:+33611416100"
                    className="inline-flex items-center gap-2.5 px-5 py-3 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:bg-primary/90 transition-colors shadow-lg shadow-primary/30"
                  >
                    <Phone className="w-4 h-4" />
                    06 11 41 61 00
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Devis;
